import { useEffect, useState } from "react";
import { History as HistoryIcon, Plus, RotateCcw, Eye, X } from "lucide-react";
import { EditorContent, useEditor, type JSONContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { TableKit } from "@tiptap/extension-table";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import { api, json } from "./api";
import { Modal, Spinner, Empty, dateLabel } from "./components";
import { ExportProject } from "./ProjectTransfer";
import { SceneEditor } from "./game/SceneEditor";
import type { Project, Board } from "./types";
import type { Scene } from "./game/types";
import type { Checkpoint, CheckpointDetail, Snapshot } from "./historyTypes";

function ReadDocument({ content }: { content: JSONContent }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false } }),
      Image,
      TableKit,
      TaskList,
      TaskItem.configure({ nested: true }),
    ],
    content,
    editable: false,
  });
  return (
    <div
      className="document-body snapshot-document"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("a")) e.preventDefault();
      }}
    >
      <EditorContent editor={editor} />
    </div>
  );
}
function ReadBoard({ board, snapshot }: { board: Board; snapshot: Snapshot }) {
  const nodes = board.content.nodes;
  if (!nodes.length) return <Empty title="Пустая доска" />;
  const minX = Math.min(...nodes.map((n) => n.x)) - 30,
    minY = Math.min(...nodes.map((n) => n.y)) - 30,
    width = Math.max(...nodes.map((n) => n.x + n.width)) - minX + 30,
    height = Math.max(...nodes.map((n) => n.y + n.height)) - minY + 30;
  return (
    <div className="snapshot-board-wrap">
      <div className="snapshot-board" style={{ width, height }}>
        <svg width={width} height={height} className="snapshot-edges">
          {board.content.edges.map((e) => {
            const a = nodes.find((n) => n.id === e.from),
              b = nodes.find((n) => n.id === e.to);
            return a && b ? (
              <g key={e.id}>
                <line
                  x1={a.x + a.width / 2 - minX}
                  y1={a.y + a.height / 2 - minY}
                  x2={b.x + b.width / 2 - minX}
                  y2={b.y + b.height / 2 - minY}
                  stroke="var(--muted)"
                />
                <text
                  x={(a.x + b.x + a.width / 2 + b.width / 2) / 2 - minX}
                  y={(a.y + b.y + a.height / 2 + b.height / 2) / 2 - minY}
                  fill="var(--text)"
                >
                  {e.label}
                </text>
              </g>
            ) : null;
          })}
        </svg>
        {nodes.map((n) => (
          <div
            key={n.id}
            className={`snapshot-card ${n.color}`}
            style={{
              left: n.x - minX,
              top: n.y - minY,
              width: n.width,
              height: n.height,
            }}
          >
            {n.type === "image" ? (
              <img
                src={`/api/projects/${snapshot.project.id}/assets/${n.targetId}`}
                alt={
                  snapshot.assets.find((a) => a.id === n.targetId)?.name ||
                  "Изображение"
                }
              />
            ) : (
              <>
                <small>
                  {
                    {
                      note: "Заметка",
                      group: "Группа",
                      document: "Документ",
                      entity: "Сущность",
                      image: "Изображение",
                    }[n.type]
                  }
                </small>
                <p>
                  {n.type === "document"
                    ? snapshot.documents.find((d) => d.id === n.targetId)?.title
                    : n.type === "entity"
                      ? snapshot.entities.find((e) => e.id === n.targetId)
                          ?.title
                      : n.text}
                </p>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
export function FrozenScene({
  snapshot,
  sceneId,
}: {
  snapshot: Snapshot;
  sceneId: string;
}) {
  const [selected, setSelected] = useState(sceneId),
    scenes = snapshot.scenes.filter((s) => !s.deletedAt),
    scene =
      scenes.find((s) => s.id === selected) ||
      scenes.find((s) => s.id === sceneId);
  if (!scene) return <Empty title="Сцена недоступна в этой версии" />;
  return (
    <div className="frozen-scene">
      <SceneEditor
        key={scene.id}
        initial={scene}
        scenes={scenes}
        entities={snapshot.entities.filter((e) => !e.deletedAt)}
        assets={snapshot.assets}
        readOnly
        onSelect={(s: Scene) => setSelected(s.id)}
        onCreate={() => {}}
        onSaved={() => {}}
        onReload={async () => {}}
      />
    </div>
  );
}
export function SnapshotPreview({ detail }: { detail: CheckpointDetail }) {
  const s = detail.snapshot,
    [selected, setSelected] = useState(""),
    [trash, setTrash] = useState(false);
  const groups = [
      { type: "document", name: "Документы", items: s.documents },
      { type: "board", name: "Доски", items: s.boards },
      { type: "entity", name: "Сущности", items: s.entities },
      { type: "scene", name: "Сцены", items: s.scenes },
    ],
    [type, id] = selected.split(":"),
    doc = s.documents.find((d) => d.id === id),
    entity = s.entities.find((e) => e.id === id),
    board = s.boards.find((b) => b.id === id);
  return (
    <div className="snapshot-preview">
      <aside>
        <span className="eyebrow">МАТЕРИАЛЫ ВЕРСИИ</span>
        <label className="snapshot-trash">
          <input
            type="checkbox"
            checked={trash}
            onChange={(e) => setTrash(e.target.checked)}
          />
          Показывать корзину
        </label>
        {groups.map((g) => (
          <section key={g.type}>
            <h4>{g.name}</h4>
            {g.items
              .filter((r) => trash || !r.deletedAt)
              .map((r) => (
                <button
                  key={r.id}
                  aria-pressed={selected === `${g.type}:${r.id}`}
                  onClick={() => setSelected(`${g.type}:${r.id}`)}
                >
                  {r.title}
                  {r.deletedAt ? " · корзина" : ""}
                </button>
              ))}
          </section>
        ))}
        <button
          aria-pressed={selected === "assets"}
          onClick={() => setSelected("assets")}
        >
          Изображения · {s.assets.length}
        </button>
      </aside>
      <main>
        {!selected ? (
          <Empty title={s.project.title}>
            Выбери материал для просмотра. Содержимое этой версии сохранено и
            доступно только для чтения.
          </Empty>
        ) : type === "document" && doc ? (
          <>
            <h2>{doc.title}</h2>
            <ReadDocument key={doc.id} content={doc.content} />
          </>
        ) : type === "entity" && entity ? (
          <div className="snapshot-entity">
            <h2>{entity.title}</h2>
            <p>{entity.description}</p>
            {entity.imageId && (
              <img
                src={`/api/projects/${s.project.id}/assets/${entity.imageId}`}
                alt={entity.title}
              />
            )}
            <dl>
              {entity.properties.map((p) => (
                <div key={p.id}>
                  <dt>{p.name}</dt>
                  <dd>
                    {p.type === "reference" &&
                    typeof p.value === "object" &&
                    p.value ? (
                      [
                        ...s.documents,
                        ...s.entities,
                        ...s.assets.map((a) => ({ ...a, title: a.name })),
                      ].find((r) => r.id === (p.value as { id: string }).id)
                        ?.title || "Материал недоступен"
                    ) : p.type === "image" && p.value ? (
                      <img
                        src={`/api/projects/${s.project.id}/assets/${p.value}`}
                        alt={p.name}
                      />
                    ) : (
                      String(p.value ?? "—")
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ) : type === "board" && board ? (
          <>
            <h2>{board.title}</h2>
            <ReadBoard board={board} snapshot={s} />
          </>
        ) : type === "scene" ? (
          <FrozenScene key={id} snapshot={s} sceneId={id} />
        ) : selected === "assets" ? (
          <div className="snapshot-images">
            {s.assets.map((a) => (
              <figure key={a.id}>
                <img
                  src={`/api/projects/${s.project.id}/assets/${a.id}`}
                  alt={a.name}
                />
                <figcaption>{a.name}</figcaption>
              </figure>
            ))}
          </div>
        ) : null}
      </main>
    </div>
  );
}
export function Versions({
  project,
  onRestored,
}: {
  project: Project;
  onRestored: (p: Project) => Promise<void>;
}) {
  const [list, setList] = useState<Checkpoint[]>([]),
    [detail, setDetail] = useState<CheckpointDetail | null>(null),
    [creating, setCreating] = useState(false),
    [restore, setRestore] = useState<Checkpoint | null>(null),
    [title, setTitle] = useState(""),
    [description, setDescription] = useState(""),
    [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const path = `/projects/${project.id}/checkpoints`;
  const load = async () => {
    setList(await api<Checkpoint[]>(path));
  };
  useEffect(() => {
    let alive = true;
    api<Checkpoint[]>(path)
      .then((v) => {
        if (alive) setList(v);
      })
      .catch((e) => {
        if (alive) setError(e.message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [path]);
  return (
    <div className="history-page">
      <div className="history-heading">
        <div>
          <span className="eyebrow">ИСТОРИЯ ПРОЕКТА</span>
          <h1>Контрольные версии</h1>
          <p className="muted">
            Сохраняй этапы работы и возвращайся к ним вместе со всеми связями и
            настройками.
          </p>
        </div>
        <div className="history-actions">
          <ExportProject projectId={project.id} />
          <button
            className="primary"
            disabled={project.archived || busy}
            onClick={() => {
              setTitle("");
              setDescription("");
              setCreating(true);
              setError("");
            }}
          >
            <Plus size={16} />
            Сохранить версию
          </button>
        </div>
      </div>
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}
      {loading ? (
        <Spinner />
      ) : !list.length ? (
        <Empty title="Сохрани первую контрольную версию">
          Дай ей понятное название, например «Движение и сбор предметов».
        </Empty>
      ) : (
        <div className="checkpoint-list">
          {list.map((c) => (
            <article key={c.id} className="checkpoint-row">
              <div className="checkpoint-symbol">
                <HistoryIcon size={21} />
              </div>
              <div className="checkpoint-info">
                <h3>{c.title}</h3>
                <small>
                  {dateLabel(c.createdAt)} ·{" "}
                  {new Date(c.createdAt).toLocaleTimeString("ru", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </small>
                {c.description && <p>{c.description}</p>}
                <div className="checkpoint-counts">
                  <span>Документы {c.counts.documents}</span>
                  <span>Доски {c.counts.boards}</span>
                  <span>Сущности {c.counts.entities}</span>
                  <span>Сцены {c.counts.scenes}</span>
                  <span>Изображения {c.counts.assets}</span>
                </div>
              </div>
              <div className="history-actions">
                <button
                  className="secondary"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    setError("");
                    try {
                      setDetail(await api<CheckpointDetail>(`${path}/${c.id}`));
                    } catch (e) {
                      setError((e as Error).message);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  <Eye size={15} />
                  Просмотр
                </button>
                <button
                  className="text-button"
                  disabled={project.archived || busy}
                  onClick={() => {
                    setRestore(c);
                    setError("");
                  }}
                >
                  <RotateCcw size={14} />
                  Восстановить
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
      {creating && (
        <Modal
          title="Сохранить контрольную версию"
          close={() => {
            if (!busy) setCreating(false);
          }}
        >
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError("");
              try {
                await api(path, json("POST", { title, description }));
                await load();
                setCreating(false);
              } catch (e) {
                setError((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <label>
              Название версии
              <input
                autoFocus
                required
                maxLength={160}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Что готово на этом этапе?"
              />
            </label>
            <label>
              Описание
              <textarea
                maxLength={4000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ключевые изменения и цель версии"
              />
            </label>
            <p className="muted">
              Версия сохранит документы, доски, сущности, сцены, изображения и
              корзину.
            </p>
            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => setCreating(false)}
              >
                Отмена
              </button>
              <button className="primary" disabled={busy || !title.trim()}>
                {busy ? <Spinner /> : <Plus size={15} />}Сохранить версию
              </button>
            </div>
          </form>
        </Modal>
      )}
      {restore && (
        <Modal
          title="Восстановить версию?"
          close={() => {
            if (!busy) setRestore(null);
          }}
        >
          <p>
            Материалы проекта будут заменены содержимым версии{" "}
            <strong>«{restore.title}»</strong>. Текущее состояние автоматически
            сохранится отдельной контрольной версией.
          </p>
          <p className="muted">
            История версий и записи плейтестов сохранятся. Открытые в других
            окнах материалы нужно будет обновить.
          </p>
          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}
          <div className="modal-actions">
            <button
              className="secondary"
              disabled={busy}
              onClick={() => setRestore(null)}
            >
              Отмена
            </button>
            <button
              className="primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError("");
                try {
                  const r = await api<{ project: Project }>(
                    `${path}/${restore.id}/restore`,
                    json("POST", { projectVersion: project.version }),
                  );
                  await onRestored(r.project);
                  setRestore(null);
                  setDetail(null);
                  await load();
                } catch (e) {
                  setError((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? <Spinner /> : <RotateCcw size={15} />}Восстановить версию
            </button>
          </div>
        </Modal>
      )}
      {detail && (
        <div className="snapshot-overlay">
          <header>
            <div>
              <span className="eyebrow">
                КОНТРОЛЬНАЯ ВЕРСИЯ · ТОЛЬКО ЧТЕНИЕ
              </span>
              <h2>{detail.title}</h2>
            </div>
            <button className="secondary" onClick={() => setDetail(null)}>
              <X size={16} />
              Закрыть просмотр
            </button>
          </header>
          <SnapshotPreview key={detail.id} detail={detail} />
        </div>
      )}
    </div>
  );
}
